/**
 * Guardar el contenido del proyecto desde el panel sin pisar al editor.
 *
 * El editor y el panel escriben el MISMO bloque de datos —colecciones, fichas,
 * barra y pie— y cada uno lo manda entero con la copia que tiene. El editor ya
 * le dice al servidor sobre qué versión trabajó, y el servidor rechaza con 409
 * si entretanto cambió; el panel mandaba el bloque sin versión, así que con el
 * editor abierto en otra pestaña lo pisaba sin aviso.
 *
 * Dos cosas hacen falta, y van juntas:
 *
 *  • Mandar la versión. Sale de la lectura del proyecto y, tras cada guardado,
 *    de lo que contesta el servidor.
 *  • Guardar de uno en uno. El panel puede lanzar varios guardados seguidos —
 *    borrar tres colecciones deprisa—; si salieran a la vez con la MISMA versión
 *    de partida, el segundo chocaría con el primero y se daría por conflicto lo
 *    que es uno mismo. En cola, cada uno parte de la versión que dejó el
 *    anterior. Y cada uno manda el contenido entero de su momento, así que uno
 *    posterior ya incluye a los anteriores.
 *
 * Tras un choque no se sigue guardando: lo que quede en cola se descarta hasta
 * que se vuelva a leer el proyecto (`resume`). Seguir mandando con la copia vieja
 * es exactamente lo que se quería evitar.
 */
export type SaveReply = { dataRevision?: string | undefined }

export function createProjectDataSaver({ send, isConflict, onConflict, onError }: {
  /** La llamada al servidor, con la versión sobre la que se editó si se conoce. */
  send: (projectId: string, data: unknown, baseRevision: string | undefined) => Promise<SaveReply>
  /** Si ese error es «el proyecto cambió en otro sitio». */
  isConflict: (error: unknown) => boolean
  onConflict: () => void
  onError: (error: unknown) => void
}) {
  let known: { projectId: string; revision: string } | null = null
  let blocked = false
  let chain: Promise<void> = Promise.resolve()

  return {
    /** La versión que trae una lectura del proyecto. Sin ella, se guarda como antes. */
    setRevision(projectId: string, revision: string | undefined) {
      known = revision ? { projectId, revision } : null
    },
    /** Tras leer de nuevo el proyecto, vuelve a dejar guardar. */
    resume() { blocked = false },
    save(projectId: string, data: unknown): Promise<void> {
      const run = async () => {
        if (blocked) return
        // Una versión de otro proyecto no vale: se abrió otro mientras tanto.
        const base = known && known.projectId === projectId ? known.revision : undefined
        try {
          const reply = await send(projectId, data, base)
          if (reply.dataRevision) known = { projectId, revision: reply.dataRevision }
        } catch (error) {
          if (isConflict(error)) {
            blocked = true
            known = null
            onConflict()
          } else {
            onError(error)
          }
        }
      }
      chain = chain.then(run, run)
      return chain
    },
  }
}
